import { useCallback, useEffect, useMemo, useRef } from "react";
import { useConnectionState } from "../store/useAppStore";
import { useErrorRecovery } from "../hooks/useErrorRecovery";
import { useConnection } from "../hooks/useConnection";
import { useSocketConnection } from "../hooks/useSocketConnection";
import { wsLogger } from "../utils/socketLogger";
import ToastContainer from "../components/Common/ToastContainer";
import ErrorBoundary from "../components/Common/ErrorBoundary";

// Import new state components
import { 
  DisconnectedState, 
  LoadingState, 
  QRCodeState, 
  ConnectedState 
} from "../components/States";

// Application States Enum for better type safety
enum AppState {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting', 
  WAITING_QR = 'waiting_qr',
  CONNECTED = 'connected'
}

const AppV2 = () => {
  // Hooks
  const { isConnected, isLoading, qrCode, authId, setConnectionStatus, setQRCode, setLoading } = useConnectionState();
  const { isOnline } = useErrorRecovery();
  const { startConnection, fetchQRCodeFallback } = useConnection();
  
  // Initialize WebSocket connection
  const socketConnection = useSocketConnection({
    autoConnect: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 2000
  });

  // Store stable reference to startConnection
  const startConnectionRef = useRef(startConnection);
  startConnectionRef.current = startConnection;

  // Enhanced connection initialization with structured logging
  const hasTriedConnection = useRef(false);
  const hasTriedInitialStatusCheck = useRef(false);
  const lastAuthId = useRef(authId);

  // Shared status check function
  const performStatusCheck = useCallback(async (context: string = 'initial') => {
    if (!authId) {
      return false;
    }

    console.log(`🔍 Performing ${context} status check...`);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout
      
      const response = await fetch(`${(import.meta as any).env.VITE_BACKEND_URL}/api/whatsapp/${authId}/status`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        
        console.log(`📊 Status check result (${context}):`, data);
        
        if (data.success && data.data?.status === 'connected') {
          console.log(`✅ Connection confirmed via ${context} check!`);
          setConnectionStatus(true);
          setLoading(false);
          return true;
        }
      }
      
      console.log(`ℹ️ No connection found in ${context} check`);
      return false;
    } catch (error) {
      const err = error as Error;
      if (err.name !== 'AbortError') {
        console.log(`⚠️ ${context} status check failed:`, error);
      }
      return false;
    }
  }, [authId, setConnectionStatus, setLoading]);

  // Initial status check on app load
  useEffect(() => {
    const performInitialStatusCheck = async () => {
      if (!authId || hasTriedInitialStatusCheck.current || isConnected) {
        return;
      }

      hasTriedInitialStatusCheck.current = true;
      await performStatusCheck('initial');
    };

    // Delay initial check to allow app to settle
    const initialCheckTimer = setTimeout(performInitialStatusCheck, 1000);
    return () => clearTimeout(initialCheckTimer);
  }, [authId, isConnected, performStatusCheck]);

  // Enhanced verification when WebSocket connects but frontend hasn't detected connection
  useEffect(() => {
    let verificationInterval: number;
    let checksPerformed = 0;
    const maxChecks = 5; // Reduzido de 10 para 5 checks

    if (socketConnection.isConnected && !isConnected && authId) {
      console.log('🔄 WebSocket connected but frontend not detected - starting verification polling');

      const verifyConnection = async () => {
        checksPerformed++;
        const confirmed = await performStatusCheck('websocket-verification');

        if (confirmed || checksPerformed >= maxChecks) {
          clearInterval(verificationInterval);
          if (checksPerformed >= maxChecks) {
            console.log(`⏰ Verification polling stopped after ${maxChecks} checks`);
          }
        }
      };

      // Check immediately, then every 10 seconds (increased from 3s)
      verifyConnection();
      verificationInterval = window.setInterval(verifyConnection, 10000);

      // Stop after 50 seconds (5 checks x 10s)
      setTimeout(() => {
        if (verificationInterval) {
          clearInterval(verificationInterval);
          console.log('⏰ Verification polling timeout reached (50s)');
        }
      }, 50000);
    }

    return () => {
      if (verificationInterval) {
        clearInterval(verificationInterval);
      }
    };
  }, [socketConnection.isConnected, isConnected, authId, performStatusCheck]);

  useEffect(() => {
    // Reset connection attempt when authId changes
    if (lastAuthId.current !== authId) {
      hasTriedConnection.current = false;
      hasTriedInitialStatusCheck.current = false;
      lastAuthId.current = authId;
    }

    const shouldConnect = authId && !isConnected && !qrCode && !hasTriedConnection.current;
    
    wsLogger.stateChanged(
      `connected:${isConnected},loading:${isLoading}`,
      `shouldConnect:${shouldConnect}`,
      authId
    );

    if (shouldConnect) {
      hasTriedConnection.current = true;
      
      wsLogger.performance('app_connection_init_start', 0, authId, {
        hasAuthId: !!authId,
        isConnected,
        hasQrCode: !!qrCode,
        isLoading
      });
      
      startConnectionRef.current();
    } else {
      wsLogger.eventReceived('connection_init_skipped', {
        reason: !authId ? 'no_auth_id' : 
                isConnected ? 'already_connected' :
                qrCode ? 'has_qr_code' :
                hasTriedConnection.current ? 'already_tried' : 'unknown',
        hasAuthId: !!authId,
        isConnected,
        hasQrCode: !!qrCode,
        isLoading
      }, authId);
    }
  }, [authId, isConnected, qrCode]);

  // QR Code fallback mechanism - only when not connected (increased delay to trust WebSocket first)
  useEffect(() => {
    // Don't trigger QR fallback if already connected
    if (isConnected) {
      return;
    }

    if (authId && socketConnection.isConnected && isLoading && !qrCode) {
      // Aumentado de 3s para 15s - confiar no WebSocket primeiro
      const fallbackTimer = setTimeout(async () => {
        // Double-check we're still not connected before fetching QR
        if (!isConnected && !qrCode) {
          console.log('🔄 [AppV2] QR code fallback triggered after 15s - fetching via HTTP...');
          await fetchQRCodeFallback();
        }
      }, 15000);

      return () => {
        clearTimeout(fallbackTimer);
      };
    }
  }, [authId, socketConnection.isConnected, isLoading, qrCode, isConnected, fetchQRCodeFallback]);

  // Reset loading state if we become connected but still loading
  useEffect(() => {
    if (isConnected && isLoading) {
      console.log('🧹 [AppV2] Connected but still loading - resetting loading state');
      setLoading(false);
    }
  }, [isConnected, isLoading, setLoading]);

  // Safety timeout to reset loading state if it gets stuck
  useEffect(() => {
    if (isLoading && !isConnected) {
      const loadingTimeout = setTimeout(() => {
        console.log('⚠️ [AppV2] Loading timeout reached (30s) - resetting state');
        setLoading(false);

        // Se não recebeu QR em 30s, há um problema
        if (!qrCode) {
          console.error('❌ [AppV2] QR code not generated after 30s - connection issue');
        }
      }, 30000); // Reduzido de 45s para 30s

      return () => clearTimeout(loadingTimeout);
    }
  }, [isLoading, isConnected, qrCode, setLoading]);

  // Determine current application state with improved logic
  const currentState = useMemo((): AppState => {
    // If no authId, user needs to initiate connection
    if (!authId) {
      return AppState.DISCONNECTED;
    }

    // Priority 1: If truly connected, show main interface (ignore stale QR codes)
    if (isConnected) {
      // Clear any stale QR code when connected
      if (qrCode) {
        console.log('🧹 Clearing stale QR code - user is connected');
        // Use setTimeout to avoid state update during render
        setTimeout(() => {
          setQRCode(null);
        }, 100);
      }
      return AppState.CONNECTED;
    }

    // Priority 2: If QR code exists and not connected, show QR interface
    if (qrCode && !isConnected) {
      return AppState.WAITING_QR;
    }

    // Priority 3: If loading or connecting, show loading interface
    if (isLoading) {
      return AppState.CONNECTING;
    }

    // Default to disconnected if none of the above
    return AppState.DISCONNECTED;
  }, [authId, isConnected, qrCode, isLoading]);

  // Handlers
  const handleConnect = useCallback(async () => {
    console.log('🚀 [AppV2] User initiated connection');
    await startConnection();
  }, [startConnection]);

  const handleRetryQR = useCallback(async () => {
    console.log('🔄 [AppV2] User requested QR retry');
    hasTriedConnection.current = false;
    await startConnection();
  }, [startConnection]);

  const handleLogout = useCallback(async () => {
    console.log('🚪 [AppV2] User initiated logout');
    
    try {
      // First, update local state immediately
      setConnectionStatus(false);
      setQRCode(null);
      
      // Then attempt backend logout
      const BACKEND_URL = (import.meta as any).env.VITE_BACKEND_URL;
      
      // Create timeout controller
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ authId }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        console.log('✅ [AppV2] Backend logout successful');
      } else {
        console.warn('⚠️ [AppV2] Backend logout failed, but continuing with local cleanup');
      }
    } catch (error) {
      console.error('❌ [AppV2] Logout error:', error);
      // Continue with local cleanup even if backend fails
    }

    // Clear all local storage
    localStorage.removeItem('wpp_auth_uuid');
    localStorage.removeItem('wpp_connection_state');
    
    // Force page reload to ensure complete cleanup
    console.log('🔄 [AppV2] Reloading page for complete cleanup');
    window.location.reload();
  }, [authId, setConnectionStatus, setQRCode]);

  // Debug logging
  useEffect(() => {
    console.log('🔌 [AppV2] Current state:', {
      state: currentState,
      isConnected,
      isLoading,
      hasQrCode: !!qrCode,
      authId: authId?.substring(0, 12) + '...',
      socketConnected: socketConnection.isConnected,
      isOnline
    });
  }, [currentState, isConnected, isLoading, qrCode, authId, socketConnection.isConnected, isOnline]);

  // Render appropriate state component
  const renderCurrentState = () => {
    switch (currentState) {
      case AppState.DISCONNECTED:
        return (
          <DisconnectedState 
            onConnect={handleConnect}
            isLoading={isLoading}
          />
        );

      case AppState.CONNECTING:
        return (
          <LoadingState 
            status="Conectando ao WhatsApp..."
            showDetails={true}
          />
        );

      case AppState.WAITING_QR:
        return (
          <QRCodeState 
            qrCode={qrCode!}
            onRetry={handleRetryQR}
          />
        );

      case AppState.CONNECTED:
        return (
          <ConnectedState 
            authId={authId!}
            onLogout={handleLogout}
          />
        );

      default:
        return (
          <DisconnectedState 
            onConnect={handleConnect}
            isLoading={isLoading}
          />
        );
    }
  };

  return (
    <ErrorBoundary>
      {renderCurrentState()}
      
      {/* Global components that should always be present */}
      <ToastContainer />
      
      {/* CSS for animations */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes ping {
          75%, 100% {
            transform: scale(2);
            opacity: 0;
          }
        }
        
        @keyframes pulse {
          50% {
            opacity: .5;
          }
        }
      `}</style>
    </ErrorBoundary>
  );
};

export default AppV2;